import React, { useEffect, useRef, useState } from 'react';
import { AlertIcon, CheckIcon, CloseIcon } from './icons';

// In-app replacements for alert() and confirm(), which render as browser chrome and block the page.
// Both work from anywhere (event-driven), so callers need no provider or hook.

export type ToastTone = 'error' | 'warning' | 'success' | 'info';

interface ToastRequest {
  message: string;
  tone: ToastTone;
}

interface ConfirmRequest {
  message: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (confirmed: boolean) => void;
}

const TOAST_EVENT = 'cv:toast';
const CONFIRM_EVENT = 'cv:confirm';
const TOAST_MS = 6000;

export const notify = (message: string, tone: ToastTone = 'error') =>
  window.dispatchEvent(new CustomEvent<ToastRequest>(TOAST_EVENT, { detail: { message, tone } }));

export const confirmDialog = (
  message: string,
  { confirmLabel = 'Confirm', danger = false }: { confirmLabel?: string; danger?: boolean } = {},
): Promise<boolean> => new Promise((resolve) => {
  window.dispatchEvent(new CustomEvent<ConfirmRequest>(CONFIRM_EVENT, {
    detail: { message, confirmLabel, danger, resolve },
  }));
});

interface Toast extends ToastRequest {
  id: number;
}

const Notifications: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmRequest | null>(null);
  const nextId = useRef(0);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const dismiss = (id: number) => setToasts((all) => all.filter((t) => t.id !== id));

  useEffect(() => {
    const onToast = (e: Event) => {
      const { message, tone } = (e as CustomEvent<ToastRequest>).detail;
      const id = nextId.current++;
      // A message already on screen (e.g. several rejected files) is not stacked again.
      setToasts((all) => (all.some((t) => t.message === message) ? all : [...all, { id, message, tone }]));
      setTimeout(() => dismiss(id), TOAST_MS);
    };
    const onConfirm = (e: Event) => setPendingConfirm((e as CustomEvent<ConfirmRequest>).detail);

    window.addEventListener(TOAST_EVENT, onToast);
    window.addEventListener(CONFIRM_EVENT, onConfirm);

    return () => {
      window.removeEventListener(TOAST_EVENT, onToast);
      window.removeEventListener(CONFIRM_EVENT, onConfirm);
    };
  }, []);

  useEffect(() => {
    // A destructive action must not be one stray Enter away.
    if (pendingConfirm) (pendingConfirm.danger ? cancelButtonRef : confirmButtonRef).current?.focus();
  }, [pendingConfirm]);

  const answer = (confirmed: boolean) => {
    pendingConfirm?.resolve(confirmed);
    setPendingConfirm(null);
  };

  return (
    <>
      {toasts.length > 0 && (
        <div className="cv-toasts">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`cv-toast cv-toast--${toast.tone}`}
              role={toast.tone === 'error' ? 'alert' : 'status'}
            >
              <span className="cv-toast__icon">
                {toast.tone === 'success' ? <CheckIcon size={16} /> : <AlertIcon size={16} />}
              </span>
              <span className="cv-toast__message">{toast.message}</span>
              <button type="button" className="cv-icon-btn cv-toast__close" onClick={() => dismiss(toast.id)} aria-label="Dismiss">
                <CloseIcon size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingConfirm && (
        <div className="cv-modal-backdrop" onClick={() => answer(false)}>
          <div
            className="cv-modal cv-modal--confirm"
            role="alertdialog"
            aria-modal="true"
            aria-label={pendingConfirm.message}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape') answer(false); }}
          >
            <p className="cv-confirm__message">{pendingConfirm.message}</p>
            <div className="cv-confirm__actions">
              <button ref={cancelButtonRef} type="button" className="cv-tool-btn" onClick={() => answer(false)}>Cancel</button>
              <button
                ref={confirmButtonRef}
                type="button"
                className={`cv-tool-btn ${pendingConfirm.danger ? 'cv-tool-btn--danger' : 'cv-tool-btn--primary'}`}
                onClick={() => answer(true)}
              >
                {pendingConfirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notifications;
